import axios from 'axios';

import type { PessoaMencionada } from './comentariosApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Comentários em PEDIDO e REQUISIÇÃO do Dashboard de Compras (at-129).
 *
 * Mesmo desenho dos comentários do veículo (ADR 0042): imutáveis, thread plano, e quem escreve é
 * decidido pela matriz configurável na Administração do Sistema → Compras.
 *
 * ⚠️ NENHUMA função aqui tem catch: "ninguém comentou neste pedido" e "não consegui perguntar"
 * levam o comprador a conclusões opostas. Quem trata o erro é a tela (regra da at-60).
 */
export type TipoDeReferenciaDeCompra = 'pedido' | 'requisicao';

export interface RespostaDeCompra {
  id: string;
  texto: string;
  autorId: string | null;
  autorNome: string;
  criadoEm: string;
  mencionados: PessoaMencionada[];
}

export interface ThreadDeCompra extends RespostaDeCompra {
  /** De onde a conversa veio — num pedido, as da requisição de origem vêm junto, marcadas. */
  origem: 'PEDIDO' | 'REQUISICAO';
  /** Rótulo pronto do servidor: "Pedido 35382", "Requisição 6768". */
  origemRotulo: string;
  respostas: RespostaDeCompra[];
}

export interface ComentariosDeCompra {
  tipo: TipoDeReferenciaDeCompra;
  id: string;
  rotulo: string;
  /** Permissões EFETIVAS, vindas da matriz. É por elas que a seção mostra o campo de escrita. */
  podeComentar: boolean;
  podeResponder: boolean;
  threads: ThreadDeCompra[];
}

export const obterComentariosDeCompra = async (
  tipo: TipoDeReferenciaDeCompra,
  id: string,
): Promise<ComentariosDeCompra> => {
  const { data } = await axios.get(`${BACKEND_URL}/comentarios-compra/referencia/${tipo}/${id}`, {
    headers: authHeader(),
  });
  return data;
};

export const comentarEmCompra = async (
  tipo: TipoDeReferenciaDeCompra,
  id: string,
  texto: string,
  mencionados: string[] = [],
): Promise<ThreadDeCompra> => {
  const { data } = await axios.post(
    `${BACKEND_URL}/comentarios-compra/referencia/${tipo}/${id}`,
    { texto, mencionados },
    { headers: authHeader() },
  );
  return data;
};

/** Thread plano: a resposta é sempre ao comentário ORIGINAL. */
export const responderComentarioDeCompra = async (
  comentarioId: string,
  texto: string,
  mencionados: string[] = [],
): Promise<RespostaDeCompra> => {
  const { data } = await axios.post(
    `${BACKEND_URL}/comentarios-compra/${comentarioId}/respostas`,
    { texto, mencionados },
    { headers: authHeader() },
  );
  return data;
};

// ------------------------------------------------------------------ configuração (ADMIN/TI)

export type AcaoDeComentarioDeCompra = 'COMENTAR_COMPRA' | 'RESPONDER_COMPRA';

export interface PermissaoDeComentarioDeCompra {
  acao: AcaoDeComentarioDeCompra;
  papeis: string[];
}

export const listarPermissoesDeComentarioDeCompra = async (): Promise<PermissaoDeComentarioDeCompra[]> => {
  const { data } = await axios.get(`${BACKEND_URL}/comentarios-compra/admin/permissoes`, {
    headers: authHeader(),
  });
  return data;
};

/** Lista vazia nega a todos, menos administradores — a matriz é deny-by-default. */
export const definirPermissaoDeComentarioDeCompra = async (
  acao: AcaoDeComentarioDeCompra,
  papeis: string[],
): Promise<void> => {
  await axios.put(
    `${BACKEND_URL}/comentarios-compra/admin/permissoes/${acao}`,
    { papeis },
    { headers: authHeader() },
  );
};
